/**
 * NewToolPage — /agents/tools/new
 * Tool creation on the FormPage template. Demo page: /form-layouts (sectioned form with a side card). The aside is the
 * Field Help panel (follows the focused field) with the assistant one click away (entity-management §2.3).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWatch } from 'react-hook-form';
import { Bot } from 'lucide-react';
import { createToolMutation, listToolsQueryKey } from '../../generated/api/@tanstack/react-query.gen.js';
import { Form, FormActions, useZodForm } from '../../components/form/index.js';
import { Button, useToast } from '../../components/ui/index.js';
import { FormPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { EntityAssistantDrawer } from '../../components/assistant/EntityAssistantDrawer.js';
import { PAGE_HELP } from '../../help/index.js';
import { TOOL_FIELD_HELP } from '../../help/tool-fields.js';
import {
  EMPTY_TOOL_VALUES,
  ToolFields,
  toolAssistantPatch,
  toolAssistantValues,
  toolFormSchema,
  toolFormToUpsert,
  type ToolFormValues,
} from './ToolForm.js';
import { FieldHelpAside, useFocusedHelpField } from './entity-ui/FieldHelpAside.js';

const LIST_PATH = '/agents/tools';

export const NewToolPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const save = useMutation(createToolMutation());
  // Stable for this visit: the assistant keeps one conversation per new-tool session.
  const [draftKey] = useState(() => crypto.randomUUID());
  const [revision, setRevision] = useState(0);
  const { focusedField, onFocus } = useFocusedHelpField();

  const form = useZodForm(toolFormSchema, { defaultValues: EMPTY_TOOL_VALUES });
  const watched = useWatch({ control: form.control });

  const applyAssistantPatch = (partial: Record<string, unknown>) => {
    form.reset({ ...form.getValues(), ...toolAssistantPatch(partial) }, { keepDefaultValues: true });
    setRevision((current) => current + 1);
  };

  const submit = async (values: ToolFormValues) => {
    // Create-only: the server answers 409 on `name` when it is taken, and `<Form>` puts that under the field.
    await save.mutateAsync({ body: { name: values.name, ...toolFormToUpsert(values) } });
    await queryClient.invalidateQueries({ queryKey: listToolsQueryKey() });
    notify('Tool created');
    navigate(`${LIST_PATH}/${values.name}`);
  };

  return (
    <Can policy="CanManageSkills">
      <FormPage
        title="New tool"
        subtitle="Register a capability agents can call. Declare its input schema so the agent knows how to use it."
        breadcrumbs={[
          { label: 'Libraries', to: '/agents/libraries' },
          { label: 'Tools', to: LIST_PATH },
        ]}
        helpContent={PAGE_HELP['tools']}
        backTo={LIST_PATH}
        backLabel="Tools"
        aside={
          <FieldHelpAside
            helpMap={TOOL_FIELD_HELP}
            labels={{ inputSchema: 'Input schema', outputSchema: 'Output schema' }}
            focusedField={focusedField}
            overviewTitle="Tools"
            overviewBody="Tools are the capabilities agents use to act: fetch data, compute, send messages, execute. Pick a name, describe what the tool does, write the input schema, and any template can reference it."
            assistant={
              <div className="space-y-3">
                <p className="text-sm text-body">Not sure where to start? Ask what this tool should do.</p>
                <EntityAssistantDrawer
                  subjectType="tool"
                  draftKey={draftKey}
                  entryPoint="tool-editor"
                  currentFormValues={toolAssistantValues({ ...EMPTY_TOOL_VALUES, ...watched } as ToolFormValues)}
                  fieldFocus={focusedField ?? undefined}
                  onApplyFieldUpdate={applyAssistantPatch}
                  trigger={(open) => (
                    <Button variant="secondary" size="sm" onClick={open}>
                      <Bot size={14} aria-hidden="true" />
                      Ask assistant
                    </Button>
                  )}
                />
              </div>
            }
          />
        }
      >
        <Form form={form} onSubmit={submit} onFocus={onFocus} guardUnsaved className="space-y-8">
          <ToolFields nameEditable currentCategory={watched.category} revision={revision} />
          <FormActions submitLabel="Create tool" cancelTo={LIST_PATH} />
        </Form>
      </FormPage>
    </Can>
  );
};

export default NewToolPage;
